import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import logo from "./assets/wgwitlogov1white-02.png";

function Login() {
  const navigate = useNavigate();
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Pull an email string from any of several common shapes
  const extractEmail = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (typeof obj.email === "string" && obj.email) return obj.email;
    if (typeof obj.emailLower === "string" && obj.emailLower) return obj.emailLower;
    if (typeof obj.primaryEmail === "string" && obj.primaryEmail) return obj.primaryEmail;
    if (obj.contact && typeof obj.contact.email === "string" && obj.contact.email) {
      return obj.contact.email;
    }
    if (typeof obj.authEmail === "string" && obj.authEmail) return obj.authEmail;
    return null;
  };

  // Ensure a public mirror doc exists for future username logins
  async function ensurePublicMirror(db, user) {
    try {
      const pubRef = doc(db, "user_public", user.uid);
      await setDoc(
        pubRef,
        { emailLower: (user.email || "").toLowerCase() },
        { merge: true }
      );
    } catch (e) {
      console.warn("ensurePublicMirror failed:", e);
    }
  }

  // Resolve user input to an email address
  const resolveToEmail = async (input) => {
    const raw = input.trim();
    if (!raw) throw new Error("Enter your username or email.");

    if (raw.includes("@")) return raw;

    const unameLower = raw.toLowerCase();
    try {
      const unameRef = doc(db, "usernames", unameLower);
      const unameSnap = await getDoc(unameRef);

      if (!unameSnap.exists()) {
        throw new Error("We couldn't find that username. Try your email or reset your password.");
      }

      const unameData = unameSnap.data() || {};
      const fromUsernames = extractEmail(unameData);
      if (fromUsernames) return String(fromUsernames);

      if (unameData.uid) {
        try {
          const pubRef = doc(db, "user_public", String(unameData.uid));
          const pubSnap = await getDoc(pubRef);
          if (pubSnap.exists()) {
            const pubData = pubSnap.data() || {};
            const fromPublic = extractEmail(pubData);
            if (fromPublic) return String(fromPublic);
          }
        } catch (e) {
          console.warn("user_public read failed:", e);
        }
      }

      throw new Error(
        "This username isn’t fully set up. Try logging in with your email or reset your password."
      );
    } catch (e) {
      if (e?.message) throw e;
      throw new Error(
        "This account needs a quick update. Try logging in with your email instead, or reset your password."
      );
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const email = await resolveToEmail(usernameOrEmail);
      const { user } = await signInWithEmailAndPassword(auth, email, password.trim());

      await user.reload();
      if (!user.emailVerified) {
        await signOut(auth);
        navigate("/verify-email");
        return;
      }

      await ensurePublicMirror(db, user);

      const userDocRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userDocRef);

      if (!userSnap.exists()) {
        setError("User data not found.");
        return;
      }

      const role = userSnap.data().role || "user";
      navigate(role === "grader" ? "/grader-dashboard" : "/user-dashboard");
    } catch (err) {
      console.error("Login error:", err);
      const code = err?.code || "";
      const msg =
        code === "auth/invalid-credential" || code === "auth/wrong-password"
          ? "Invalid username/email or password."
          : code === "auth/user-not-found"
          ? "Account not found."
          : code === "auth/user-disabled"
          ? "This account is disabled."
          : err?.message || "Login failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.logoContainer}>
        <img src={logo} alt="Logo" style={styles.logo} />
        <span style={styles.siteName}>pregradepros.com</span>
      </div>

      <div style={styles.container}>
        <button onClick={() => navigate("/")} style={styles.homeButton}>
          ← Back to Home
        </button>

        <h2 style={styles.heading}>Login</h2>
        <form onSubmit={handleLogin} style={styles.form}>
          <input
            type="text"
            placeholder="Email or Username"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            autoComplete="username"
            required
            style={styles.input}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            style={styles.input}
          />

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <p style={styles.text}>
          Don’t have an account?{" "}
          <a href="/signup" style={styles.link}>
            Sign up
          </a>{" "}
          |{" "}
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            style={styles.inlineLinkButton}
          >
            Forgot password?
          </button>
        </p>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: "100vh",
    backgroundColor: "#000",
    color: "#fff",
    fontFamily: "'Montserrat', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "80px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  logoContainer: {
    position: "absolute",
    top: "20px",
    left: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  logo: {
    height: "40px",
  },
  siteName: {
    fontSize: "20px",
    fontWeight: "bold",
  },
  container: {
    maxWidth: "460px",
    width: "100%",
    textAlign: "center",
  },
  homeButton: {
    marginBottom: "24px",
    padding: "9.2px 16.1px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "6.9px",
    cursor: "pointer",
    fontSize: "16.1px",
    fontWeight: 600,
  },
  heading: {
    fontSize: "27.6px",
    fontWeight: 700,
    marginBottom: "23px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  input: {
    padding: "13.8px",
    fontSize: "18.4px",
    borderRadius: "6.9px",
    border: "1px solid #555",
    backgroundColor: "#111",
    color: "#fff",
  },
  inlineLinkButton: {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "#4dabf7",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "16.1px",
  },
  button: {
    padding: "13.8px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "6.9px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "18.4px",
  },
  error: {
    color: "red",
    fontSize: "16.1px",
    marginTop: "-6px",
    marginBottom: "-2px",
    textAlign: "left",
  },
  text: {
    marginTop: "23px",
    fontSize: "16.1px",
  },
  link: {
    color: "#4dabf7",
    textDecoration: "underline",
  },
};

export default Login;